import React from "react";
import { CheckCheck, Copy, Loader2, RotateCcw } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { FOLLOW_UP_STATUS, hasCallablePhone } from "../../utils/follow-up";
import successToast from "../helper-components/success-toast";
import errorToast from "../helper-components/error-toast";

// compact: the size used inside table rows
const buttonBase = (compact) =>
  `inline-flex items-center justify-center whitespace-nowrap transition-colors border-radius focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--follow-up-focus-ring)] disabled:cursor-not-allowed ${
    compact
      ? "h-[28px] gap-1.5 px-2 text-[length:var(--list-table-row-font-size)] font-[var(--follow-up-label-fw)]"
      : "h-9 gap-2 px-3 text-[length:var(--follow-up-text-fs)] font-[var(--follow-up-label-fw)]"
  }`;

const iconSize = (compact) => (compact ? 14 : 16);

const Spinner = ({ compact }) => (
  <Loader2 size={iconSize(compact)} className="animate-spin" aria-hidden="true" />
);

// itemCount: the items picked for the message (when the button sends one)
export const WhatsAppButton = ({ followUp, itemCount, onClick, compact = false, className = "" }) => {
  const canMessage = hasCallablePhone(followUp?.cmr_phone_number);
  const isEnabled = canMessage && itemCount !== 0;
  const title = !canMessage
    ? "No valid phone number"
    : itemCount === 0
    ? "Tick the items to send"
    : "Send the follow-up message on WhatsApp";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isEnabled}
      title={title}
      className={`${buttonBase(compact)} ${
        isEnabled
          ? "bg-[var(--follow-up-whatsapp-bg)] text-[var(--follow-up-whatsapp-fg)] hover:bg-[var(--follow-up-whatsapp-hover-bg)]"
          : "bg-[var(--follow-up-disabled-bg)] text-[var(--follow-up-disabled-fg)]"
      } ${className}`}
    >
      <FaWhatsapp size={iconSize(compact)} aria-hidden="true" />
      {itemCount === undefined
        ? "WhatsApp"
        : `WhatsApp · ${itemCount} item${itemCount === 1 ? "" : "s"}`}
    </button>
  );
};

export const StatusToggleButton = ({ followUp, isBusy, onClick, compact = false, className = "" }) => {
  const isDone = followUp?.rfu_status === FOLLOW_UP_STATUS.done;
  return (
    <button
      type="button"
      onClick={() => onClick(followUp)}
      disabled={isBusy}
      className={`${buttonBase(compact)} ${
        isDone
          ? "border border-[var(--follow-up-reopen-btn-border)] bg-[var(--follow-up-reopen-btn-bg)] text-[var(--follow-up-reopen-btn-fg)] hover:bg-[var(--follow-up-reopen-btn-hover-bg)]"
          : "bg-[var(--follow-up-done-btn-bg)] text-[var(--follow-up-done-btn-fg)] hover:bg-[var(--follow-up-done-btn-hover-bg)]"
      } ${className}`}
    >
      {isBusy ? (
        <Spinner compact={compact} />
      ) : isDone ? (
        <RotateCcw size={iconSize(compact)} aria-hidden="true" />
      ) : (
        <CheckCheck size={iconSize(compact)} aria-hidden="true" />
      )}
      {isDone ? "Mark Pending" : "Mark Done"}
    </button>
  );
};

export const CopyPhoneButton = ({ phone }) => {
  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(String(phone));
      successToast("Phone number copied");
    } catch (error) {
      errorToast("Couldn't copy the phone number");
    }
  };
  return (
    <button
      type="button"
      onClick={copyPhone}
      aria-label={`Copy phone number ${phone}`}
      title="Copy phone number"
      className="grid h-6 w-6 place-items-center rounded-md text-[var(--follow-up-muted-fg)] transition-colors hover:bg-[var(--follow-up-call-bg)] hover:text-[var(--follow-up-call-fg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--follow-up-focus-ring)]"
    >
      <Copy size={13} aria-hidden="true" />
    </button>
  );
};
